import PropTypes from "prop-types";
import { useCallback, useEffect, useRef } from "react";
import { bookingPropTypes, quotationPropTypes, resolutionPropType } from "app/utils/propTypes";
import {
	FLIGHT_OPTIONS_STATUS,
	OFFER_TYPES,
	PRE_BOOK_CODE_STATUS,
	PRODUCT_PAGE_CODE_STATUS,
	QUOTATION_CODE_STATUS,
	QUOTATION_STATUS_VALIDE,
	RESOLUTION,
} from "app/constants";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import { scrollToElement } from "app/utils/scroller";
import AlertMessage from "app/pages/.shared/AlertMessage";
import { FormattedMessage, useIntl } from "react-intl";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import FlexRateModalContainer from "app/pages/Booking/Quotation/FlexRate/FlexRateModalContainer";
import HolidaySummaryContainer from "app/pages/Booking/HolidaySummary/HolidaySummary/HolidaySummaryContainer";
import QuotationRoomsBoardsContainer from "app/pages/Booking/Quotation/QuotationRoomsBoards/QuotationRoomsBoardsContainer";
import QuotationFlightsContainer from "app/pages/Booking/Quotation/Flight/QuotationFlightsContainer";
import QuotationActivitiesContainer from "app/pages/Booking/Quotation/Activity/QuotationActivitiesContainer";
import QuotationSkeleton from "app/pages/Booking/Quotation/Skeletons/QuotationSkeleton";
import { isEmpty } from "lodash";
import { computeActivitiesDuplicateDates } from "app/utils/quotationUtils";
import usePrevious from "app/utils/hooks/usePrevious";
import { useLocation, useNavigate } from "react-router-dom";
import PriceChangedWarningMessage from "app/pages/Booking/Payment/PriceChangedWarningMessage";
import "./Quotation.scss";
import BookingLoadingPrice from "app/pages/Booking/BookingLoadingPrice";
import QuotationSubmitContainer from "app/pages/Booking/Quotation/Submit/QuotationSubmitContainer";
import { Form, useFormikContext } from "formik";

const Quotation = ({
	showAlert,
	quotation = {},
	booking = {},
	resolution,
	isStopover,
	enableFlexRateChoice = false,
	hasSpecialOfferOnQuote = false,
	specialOffer = {},
	isOptionFlexAvailable = false,
	showFlexRatesModal = () => {},
	fallbackRedirectUrl,
	ficheProduitStatus,
	clearSmartDP = () => {},
	priceChangeData = {},
	flightLuggageOption = {},
}) => {
	const { errors, handleSubmit, isSubmitting } = useFormikContext();
	const activitiesRef = useRef();
	const quotationRef = useRef();
	const intl = useIntl();
	const navigate = useNavigate();

	const previousStatus = usePrevious(quotation.status);
	const previousPreBookStatus = usePrevious(quotation.status);
	const previousFicheProduitStatus = usePrevious(ficheProduitStatus);

	const previousFlightOptionsStatus = usePrevious(quotation.flightOptionsStatus);

	useEffect(() => {
		const { status, preBookStatus, flightOptionsStatus } = quotation;

		if (
			status !== previousStatus ||
			preBookStatus !== previousPreBookStatus ||
			flightOptionsStatus !== previousFlightOptionsStatus ||
			ficheProduitStatus !== previousFicheProduitStatus
		) {
			if (
				status === QUOTATION_CODE_STATUS.SESSION_EXPIRED ||
				preBookStatus === PRE_BOOK_CODE_STATUS.SESSION_EXPIRED ||
				flightOptionsStatus === FLIGHT_OPTIONS_STATUS.SESSION_EXPIRED ||
				// handle /sdp/accommodationDescription expiration
				ficheProduitStatus === PRODUCT_PAGE_CODE_STATUS.SESSION_EXPIRED
			) {
				if (isSDPQuote) {
					clearSmartDP();
					const alertTitle = intl.formatMessage({ id: `alert.data.updated.title` });
					const alertMessage = intl.formatMessage({
						id: `alert.data.updated.message`,
					});

					const alertActionNodes = [
						<Button
							variant={BUTTON_VARIANTS.FILLED}
							size={BUTTON_SIZES.LARGE}
							key={"alert.data.updated.button.label"}
							onClick={() => {
								if (fallbackRedirectUrl.indexOf("https") !== -1) {
									window.location.replace(fallbackRedirectUrl);
								} else {
									navigate({ pathname: fallbackRedirectUrl }, { replace: true });
								}
							}}
							label={intl.formatMessage({
								id: `alert.data.updated.button.label`,
							})}
						/>,
					];
					showAlert({ alertTitle, alertMessage, alertActionNodes });
				} else {
					scrollToElement({ element: quotationRef.current });
					navigate("../search", {
						replace: true,
						state: {
							alertMessageId: "error.booking.session.expired",
						},
					});
				}
			}
		}

		if (errors?.duplicateDates?.length > 0) {
			scrollToElement({ element: activitiesRef.current });
		}
	}, [
		previousStatus,
		previousPreBookStatus,
		previousFlightOptionsStatus,
		quotation.status,
		quotation.preBookStatus,
		quotation.flightOptionsStatus,
		errors,
		previousFicheProduitStatus,
		ficheProduitStatus,
	]);

	const handleFlexRates = useCallback(() => {
		// si l'utilisateur a déja fait un choix on ne lui propose plus la modale lorsqu'il clique sur reserver
		showFlexRatesModal(!booking.insuranceFlex);
	}, [booking.insuranceFlex]);

	const isLoading = quotation.flightOptionsStatus === FLIGHT_OPTIONS_STATUS.LOADING;
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const shouldDisplaySpecialOfferWarning =
		specialOffer && !isEmpty(specialOffer) && !hasSpecialOfferOnQuote && !enableFlexRateChoice;

	const duplicateDates = computeActivitiesDuplicateDates(booking.activities);
	const hasDuplicateDateInActivities = duplicateDates.length > 0;

	const shouldNotDisplayModalFlex =
		!enableFlexRateChoice ||
		booking.insuranceFlex ||
		!isOptionFlexAvailable ||
		hasDuplicateDateInActivities;

	const { pathname } = useLocation();

	const isSDPQuote =
		pathname?.includes("/sdp/booking/quote") || pathname?.includes("/stopover/booking/quote");

	const selectedLuggageCount = booking?.luggage?.quantity;

	// Pour le stopover Ajout du bloc bagages UNIQUEMENT
	// SI le vol n’inclus pas déjà de bagage
	// ET que l’utilisateur n’en a pas ajouté à la mano
	const disableBaggagesSelection =
		isStopover && (flightLuggageOption?.included || selectedLuggageCount > 0);

	return (
		<Form className="quotation" onSubmit={handleSubmit} ref={quotationRef}>
			<div className="quotation__head">
				{(quotation.preBookStatus === PRE_BOOK_CODE_STATUS.FULL_PROPERTY ||
					quotation.preBookStatus === PRE_BOOK_CODE_STATUS.FULL_FLIGHT ||
					quotation.preBookStatus === PRE_BOOK_CODE_STATUS.FULL_TRANSFER) && (
					<div data-testid="booking__prebook-full-message">
						<AlertMessage
							scrollOnShow
							message={<FormattedMessage id="error.no.availabilities.search" />}
						/>
					</div>
				)}
				{!isMobile && priceChangeData.isPriceChange && (
					<PriceChangedWarningMessage
						offerType={booking?.offer?.type}
						newPrice={priceChangeData?.newPrice}
						oldPrice={priceChangeData?.oldPrice}
						messageFlightAccommodationChangeUp="error.quotation.price.changed.up.message"
						messageFlightAccommodationChangeDown="error.quotation.price.changed.down.message"
						messageAccommodationOnlyChangeUp="error.quotation.price.changed.up.message"
						messageAccommodationOnlyChangeDown="error.quotation.price.changed.down.message"
					/>
				)}
				{isMobile && !isSDPQuote && (
					<div className="quotation__my-booking">
						<HolidaySummaryContainer showProductPicture />
					</div>
				)}
			</div>

			{QUOTATION_STATUS_VALIDE.includes(quotation.status) ? (
				<>
					{shouldDisplaySpecialOfferWarning && (
						<AlertMessage
							message={
								<FormattedMessage
									id="quotation.special.offer.not.available.for.departure.date.message"
									values={{
										specialOffer: specialOffer.title,
									}}
								/>
							}
						/>
					)}

					<div className="quotation__items">
						<QuotationRoomsBoardsContainer />
						<QuotationFlightsContainer
							disableFlightsSelection={
								isStopover || booking?.offer?.type === OFFER_TYPES.ACCOMODATION_ONLY
							}
							isStopover={isStopover}
							disableBaggagesSelection={
								disableBaggagesSelection ||
								booking?.offer?.type === OFFER_TYPES.ACCOMODATION_ONLY
							}
						/>
						<QuotationActivitiesContainer activityRef={activitiesRef} error={errors} />
					</div>

					<QuotationSubmitContainer
						isLoading={isLoading}
						submitting={isSubmitting}
						enableFlexRateChoice={!shouldNotDisplayModalFlex}
						onShowFlexRateChoice={handleFlexRates}
					/>
				</>
			) : (
				<>
					{//display BookingLoadingPrice juste for booking/quote and not for /sdp/booking/quote
					isMobile && !isSDPQuote && <BookingLoadingPrice />}
					<QuotationSkeleton />
				</>
			)}

			<PushNotificationContainer />

			<FlexRateModalContainer submitting={isSubmitting} handleSubmit={handleSubmit} />
		</Form>
	);
};

Quotation.propTypes = {
	booking: bookingPropTypes,
	clearSmartDP: PropTypes.func,
	showAlert: PropTypes.func,
	showFlexRatesModal: PropTypes.func,
	flightLuggageOption: PropTypes.object,
	priceChangeData: PropTypes.object,
	quotation: quotationPropTypes,
	resolution: resolutionPropType,
	specialOffer: PropTypes.object,
	enableFlexRateChoice: PropTypes.bool,
	fallbackRedirectUrl: PropTypes.string,
	ficheProduitStatus: PropTypes.string,
	hasSpecialOfferOnQuote: PropTypes.bool,
	isOptionFlexAvailable: PropTypes.bool,
	isStopover: PropTypes.bool,
};

export default Quotation;
